import {
  ArrowUpRight,
  Boxes,
  ClipboardCheck,
  Film,
  PackageCheck,
} from "lucide-react";
import type { DashboardResponse } from "../types";
import { RunPanel } from "../components/RunPanel";
import { StatusBadge } from "../components/StatusBadge";

interface OverviewPageProps {
  dashboard: DashboardResponse;
  onOpenProduct: (productId: string) => void;
  onOpenReviews: () => void;
}

export function OverviewPage({
  dashboard,
  onOpenProduct,
  onOpenReviews,
}: OverviewPageProps) {
  const { metrics } = dashboard;
  const metricItems = [
    {
      label: "本批商品",
      value: metrics.product_count,
      suffix: "items",
      icon: Boxes,
      note: `${metrics.package_count} 个素材包就绪`,
    },
    {
      label: "脚本版本",
      value: metrics.script_count,
      suffix: "scripts",
      icon: Film,
      note: "每商品默认 3 个版本",
    },
    {
      label: "媒体任务",
      value: metrics.task_count,
      suffix: "tasks",
      icon: PackageCheck,
      note: `${metrics.mock_task_count} 个 Mock 任务`,
    },
    {
      label: "待审内容",
      value: metrics.pending_review_count,
      suffix: "reviews",
      icon: ClipboardCheck,
      note: `${metrics.approved_review_count} 个版本已通过`,
    },
  ];

  return (
    <div className="overview-layout">
      <div className="overview-main">
        <section className="metric-grid" aria-label="批次指标">
          {metricItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <article className="metric-block" key={item.label}>
                <div className="metric-topline">
                  <span>0{index + 1}</span>
                  <Icon size={19} strokeWidth={1.8} aria-hidden="true" />
                </div>
                <div className="metric-number">
                  <strong>{item.value}</strong>
                  <span>{item.suffix}</span>
                </div>
                <h2>{item.label}</h2>
                <p>{item.note}</p>
              </article>
            );
          })}
        </section>

        <section className="pipeline-panel" aria-labelledby="pipeline-title">
          <div className="section-heading">
            <div>
              <span className="section-index">Flow 02</span>
              <h2 id="pipeline-title">生产流水线</h2>
            </div>
            <span className="batch-id">BATCH / LOCAL-001</span>
          </div>
          <div className="pipeline-track">
            {dashboard.pipeline.map((step, index) => (
              <div className={`pipeline-step is-${step.status}`} key={step.id}>
                <div className="step-rail">
                  <span>{String(index + 1).padStart(2, "0")}</span>
                </div>
                <strong>{step.label}</strong>
                <StatusBadge
                  compact
                  status={
                    step.status === "completed"
                      ? "completed"
                      : step.status === "active"
                        ? "待人工审核"
                        : "未开始"
                  }
                />
              </div>
            ))}
          </div>
        </section>

        <section className="product-table-panel">
          <div className="section-heading">
            <div>
              <span className="section-index">Queue 03</span>
              <h2>商品生产队列</h2>
            </div>
            <button type="button" className="text-button" onClick={onOpenReviews}>
              查看审核队列
              <ArrowUpRight size={15} aria-hidden="true" />
            </button>
          </div>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>商品</th>
                  <th>平台 / 时长</th>
                  <th>脚本</th>
                  <th>任务</th>
                  <th>审核进度</th>
                  <th aria-label="操作" />
                </tr>
              </thead>
              <tbody>
                {dashboard.products.map((product) => (
                  <tr key={product.product_id}>
                    <td>
                      <button
                        type="button"
                        className="product-cell"
                        onClick={() => onOpenProduct(product.product_id)}
                      >
                        <span>{product.product_id}</span>
                        <div>
                          <strong>{product.product_name}</strong>
                          <small>{product.category}</small>
                        </div>
                      </button>
                    </td>
                    <td>
                      {product.platform}
                      <small className="cell-subline">{product.video_duration}s</small>
                    </td>
                    <td className="mono-cell">{product.script_count}</td>
                    <td className="mono-cell">{product.task_count}</td>
                    <td>
                      <div className="progress-copy">
                        <span>
                          {product.approved_count}/{product.review_count}
                        </span>
                        <div className="mini-progress">
                          <span
                            style={{
                              width: `${
                                product.review_count
                                  ? (product.approved_count / product.review_count) * 100
                                  : 0
                              }%`,
                            }}
                          />
                        </div>
                      </div>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="row-action"
                        onClick={() => onOpenProduct(product.product_id)}
                        aria-label={`查看 ${product.product_name}`}
                      >
                        <ArrowUpRight size={16} aria-hidden="true" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
      <aside className="overview-aside">
        <RunPanel />
        <section className="boundary-note">
          <span className="section-index">Human Gate</span>
          <h2>发布边界</h2>
          <p>
            价格、优惠、功效、承诺、违禁词、品牌调性和最终发布内容始终需要人工确认。
          </p>
          <div className="boundary-stats">
            <div>
              <strong>{metrics.pending_review_count}</strong>
              <span>待确认</span>
            </div>
            <div>
              <strong>{dashboard.validation_issues.length}</strong>
              <span>输入提示</span>
            </div>
          </div>
        </section>
      </aside>
    </div>
  );
}

